// src/app/modules/demandes/HorsCatalogueRf.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/HorsCatalogue.css";
import "../../RessourcesStyles/DemandeFormModal.css";

import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchDomainesRequest, fetchThemesRequest } from "../../../../../redux/actions/paramDomainesThemesActions";
import { getAllUsersRequest } from "../../../../../redux/actions/userActions";
import { clearDemandeMessage, createDemandeRfBulkRequest } from "../../../../../redux/actions/demandesActions";

import { Users } from "lucide-react";

const initialCommon = {
    campagneId: "",
    domaineId: "",
    themeId: "",
    action: "",
    description: "",
};

export default function HorsCatalogueRf({
    campagneId,
    hideCampagneSelect = false,
    onCancel,
    onSuccess,
}) {
    const dispatch = useDispatch();

    const campagnesState = useSelector((s) => s.campagnes || {});
    const refState = useSelector((s) => s.paramDomainesThemes || {});
    const { users } = useSelector((s) => s.user);
    const demandesState = useSelector((s) => s.demandes || {});

    const campagnes = campagnesState.visibleList || campagnesState.list || [];
    const domaines = refState.domaines || refState.domainesList || [];
    const themes = refState.themes || refState.themesList || [];

    const [common, setCommon] = useState(initialCommon);
    const [openPick, setOpenPick] = useState(false);
    const [openBulkFields, setOpenBulkFields] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const loading = !!demandesState.loading;

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(getAllUsersRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
        dispatch(clearDemandeMessage());
    }, [dispatch]);

    useEffect(() => {
        if (hideCampagneSelect && campagneId) {
            setCommon((state) => ({ ...state, campagneId: String(campagneId) }));
        }
    }, [hideCampagneSelect, campagneId]);

    useEffect(() => {
        if (!hideCampagneSelect && !common.campagneId && campagnes?.length) {
            setCommon((state) => ({ ...state, campagneId: String(campagnes[0].id) }));
        }
    }, [campagnes, common.campagneId, hideCampagneSelect]);

    useEffect(() => {
        if (!submitAttempted || loading) return;

        if (demandesState.error) {
            setSubmitAttempted(false);
            return;
        }

        if (demandesState.message) {
            setCommon((state) => ({
                ...initialCommon,
                campagneId: hideCampagneSelect && campagneId ? String(campagneId) : state.campagneId,
            }));
            setOpenPick(false);
            setOpenBulkFields(false);
            setSelectedUserIds([]);
            setSubmitAttempted(false);
            dispatch(clearDemandeMessage());
            if (onSuccess) onSuccess();
        }
    }, [campagneId, demandesState.error, demandesState.message, dispatch, hideCampagneSelect, loading, onSuccess, submitAttempted]);

    const themesFiltered = useMemo(() => {
        const domaineId = common.domaineId ? Number(common.domaineId) : null;
        if (!domaineId) return themes || [];
        return (themes || []).filter((theme) => Number(theme?.domaineId || theme?.domaine?.id) === domaineId);
    }, [themes, common.domaineId]);

    const selectedUsers = useMemo(() => {
        const set = new Set((selectedUserIds || []).map(String));
        return (users || []).filter((user) => set.has(String(user.id)));
    }, [users, selectedUserIds]);

    const update = (key, value) => setCommon((state) => ({ ...state, [key]: value }));

    const openSelector = () => {
        if (!common.campagneId) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionnez d'abord une campagne.",
                target: document.body,
            });
            return;
        }
        if (!common.action?.trim()) {
            Swal.fire({
                icon: "warning",
                text: "L'intitulé de la formation est obligatoire.",
                target: document.body,
            });
            return;
        }
        setOpenPick(true);
    };

    const onConfirmUsers = (ids) => {
        if (!ids?.length) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionnez au moins un collaborateur.",
                target: document.body,
            });
            return;
        }
        const unique = Array.from(new Set(ids || []));
        setSelectedUserIds(unique);
        setOpenPick(false);
        setOpenBulkFields(true);
    };

    const onSubmitBulkFields = (items) => {
        const payload = {
            campagneId: Number(common.campagneId),
            type: "HORS_CATALOGUE",
            domaineId: common.domaineId ? Number(common.domaineId) : null,
            themeId: common.themeId ? Number(common.themeId) : null,
            action: common.action.trim(),
            description: common.description?.trim() || null,
            items,
        };

        dispatch(clearDemandeMessage());
        setSubmitAttempted(true);
        dispatch(createDemandeRfBulkRequest(payload));
    };

    return (
        <>
            <div className="demande-form">
                <div className="field">
                    <label>Intitulé de la formation *</label>
                    <input
                        value={common.action}
                        onChange={(event) => update("action", event.target.value)}
                        placeholder="Saisissez l'intitulé de la formation"
                    />
                </div>

                <div className="demande-grid-2">
                    {!hideCampagneSelect && (
                        <div className="field">
                            <label>Campagne</label>
                            <select value={common.campagneId} onChange={(event) => update("campagneId", event.target.value)}>
                                <option value="">—</option>
                                {(campagnes || []).map((campagne) => (
                                    <option key={campagne.id} value={campagne.id}>
                                        {campagne.nom || campagne.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="field">
                        <label>Domaine</label>
                        <select
                            value={common.domaineId}
                            onChange={(event) => {
                                update("domaineId", event.target.value);
                                update("themeId", "");
                            }}
                        >
                            <option value="">—</option>
                            {(domaines || []).map((domaine) => (
                                <option key={domaine.id} value={domaine.id}>
                                    {domaine.nom || domaine.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="field">
                        <label>Thème</label>
                        <select value={common.themeId} onChange={(event) => update("themeId", event.target.value)}>
                            <option value="">—</option>
                            {(themesFiltered || []).map((theme) => (
                                <option key={theme.id} value={theme.id}>
                                    {theme.nom || theme.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label>Justification du besoin</label>
                    <textarea
                        value={common.description}
                        onChange={(event) => update("description", event.target.value)}
                        rows={5}
                    />
                </div>

                <div className="demande-actions">
                    {onCancel && (
                        <button className="btn btn-light" type="button" onClick={onCancel} disabled={loading}>
                            Annuler
                        </button>
                    )}
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={openSelector}
                        disabled={!common.campagneId || loading}
                    >
                        <Users size={16} />
                        Choisir les collaborateurs
                    </button>
                </div>

                {!common.campagneId && <div className="demande-hint">Sélectionnez d'abord une campagne.</div>}
                {demandesState.error && <div className="demande-hint">Une erreur est survenue lors de l'envoi de la demande.</div>}
            </div>

            <UsersPickerModal
                open={openPick}
                onClose={() => setOpenPick(false)}
                users={users}
                title="Sélectionner des collaborateurs"
                onConfirm={onConfirmUsers}
            />

            <BulkUsersFieldsModal
                open={openBulkFields}
                onClose={() => setOpenBulkFields(false)}
                users={selectedUsers}
                title="Paramétrage par collaborateur"
                onSubmit={onSubmitBulkFields}
            />
        </>
    );
}
